import "./App.css";
import React, { Suspense } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import FallbackSpinner from "src/@core/components/spinner";

import AuthGuard from "src/@core/components/auth/AuthGuard";
import UserLayout from "./layouts/UserLayout";
import BlankLayout from "./@core/layouts/BlankLayout";

import AclGuard from "./@core/components/auth/AclGuard";
import GuestGuard from "./@core/components/auth/GuestGuard";
import { defaultACLObj } from "./configs/acl";
import AuthLayout from "./layouts/AuthLayout";
import AdSettingsTestModule from "./pages/dashboard/general/adSettings/addSettingsTest";
import FinancePermissionGuard from "./@core/components/auth/FinancePermissionGuard";


// Import pages
const Page401 = React.lazy(() => import("./pages/401"));
const Page404 = React.lazy(() => import("./pages/404"));
const LoginPage = React.lazy(() => import("./pages/authPages/login"));
const RegisterPage = React.lazy(() => import("./pages/authPages/register"));
const ForgotPage = React.lazy(() => import("./pages/authPages/forgotpassword"));
const ResetPasswordPage = React.lazy(() => import("./pages/authPages/forgotpassword/resetpassword"));
const VerificationPage = React.lazy(() => import("./pages/authPages/register/verificationPage"));
const OverviewPage = React.lazy(() => import("./pages/dashboard/menu/overview"));
const BusinessSubscriptionPage = React.lazy(() => import("./pages/dashboard/menu/businessSubscription"));
const BusinessSubscriptionDetailsPage = React.lazy(() => import("./pages/dashboard/menu/businessSubscription/details"));
const FastMarketingPage = React.lazy(() => import("./pages/dashboard/menu/fastMarketing"));
const FastMarketingInvoicePage = React.lazy(() => import("./pages/dashboard/menu/fastMarketingInvoice"));
const OperatorWalletPage = React.lazy(() => import("./pages/dashboard/menu/operatorWallet"));
const PersoanalWalletPage = React.lazy(() => import("./pages/dashboard/menu/personalWallet"));
const BusinessWalletPage = React.lazy(() => import("./pages/dashboard/menu/businessWallet"));
const BusinessWalletDetailsPage = React.lazy(() => import("./pages/dashboard/menu/businessWallet/details"));
const BusinessWalletTxnHistoryPage = React.lazy(() => import("./pages/dashboard/menu/businessWallet/txnHistory"));
const BusinessCashOutsPage = React.lazy(() => import("./pages/dashboard/menu/businessCashOuts"));
const BusinessCashOutDetailsPage = React.lazy(() => import("./pages/dashboard/menu/businessCashOuts/details"));
const TaxSettingsPage = React.lazy(() => import("./pages/dashboard/general/taxSettings"));
const TierSettingsPage = React.lazy(() => import("./pages/dashboard/general/tierSettings"));
const AdSettingsPage = React.lazy(() => import("./pages/dashboard/general/adSettings"));
const BankSettingsPage = React.lazy(() => import("./pages/dashboard/general/bankSettings"));
const CurrencySettingsPage = React.lazy(() => import("./pages/dashboard/general/currencySettings"));
const FinanceRolesPage = React.lazy(() => import("./pages/dashboard/general/financeRoles"));
const FinanceAdminsPage = React.lazy(() => import("./pages/dashboard/general/financeAdmins"));

const Guard = ({ children, authGuard, guestGuard }) => {
  if (guestGuard) {
    return <GuestGuard fallback={<FallbackSpinner />}>{children}</GuestGuard>;
  } else if (!guestGuard && !authGuard) {
    return <>{children}</>;
  } else {
    return <AuthGuard fallback={<FallbackSpinner />}>{children}</AuthGuard>;
  }
};
function App() {
  const aclAbilities = defaultACLObj;
  return (
    <Suspense fallback={<FallbackSpinner />}>
      <AclGuard aclAbilities={aclAbilities}>
        <Routes>
          <Route
            element={
              <BlankLayout>
                <Outlet />
              </BlankLayout>
            }
          >
            <Route path="/401" element={<Page401 />} />
            <Route path="/404" element={<Page404 />} />
            <Route
              element={
                <AuthLayout>
                  <Outlet />
                </AuthLayout>
              }
            >
              <Route
                element={
                  <Guard guestGuard>
                    <Outlet />
                  </Guard>
                }
              >
                <Route path="/" element={<Navigate to={"/login"} />}></Route>
                <Route path="/login" element={<LoginPage />}></Route>
                <Route path="/register" element={<RegisterPage />}></Route>
                <Route path="/verify" element={<VerificationPage />}></Route>
                <Route path="/forgot-password" element={<ForgotPage />}></Route>
                <Route path="/reset-password" element={<ResetPasswordPage />}></Route>
              </Route>
            </Route>
          </Route>
          <Route
            element={
              <UserLayout>
                <Outlet />
              </UserLayout>
            }
          >
            <Route
              element={
                <Guard authGuard>
                  <Outlet />
                </Guard>
              }
            >
              <Route path="/dashboard" element={<Navigate to={"/overview"} />} />
              {/* Menu Routes */}
              <Route path="/overview" element={<OverviewPage />} />
              <Route
                path="/business-subscription"
                element={
                  <FinancePermissionGuard moduleKey="businessSubscriptions">
                    <BusinessSubscriptionPage />
                  </FinancePermissionGuard>
                }
              />
              <Route
                path="/business-subscription/details/:businessId"
                element={
                  <FinancePermissionGuard moduleKey="businessSubscriptions">
                    <BusinessSubscriptionDetailsPage />
                  </FinancePermissionGuard>
                }
              />
              <Route path="/fast-marketing" element={<FastMarketingPage />} />
              <Route path="/invoice/:adsId" element={<FastMarketingInvoicePage />} />
              <Route path="/operator-wallet" element={<OperatorWalletPage />} />
              <Route path="/personal-wallet" element={<PersoanalWalletPage />} />
              <Route
                path="/business-wallet"
                element={
                  <FinancePermissionGuard moduleKey="businessWallets">
                    <BusinessWalletPage />
                  </FinancePermissionGuard>
                }
              />
              <Route
                path="/business-wallet/details/:businessId"
                element={
                  <FinancePermissionGuard moduleKey="businessWallets">
                    <BusinessWalletDetailsPage />
                  </FinancePermissionGuard>
                }
              />
              <Route
                path="/business-wallet/txn-history/:locationId"
                element={
                  <FinancePermissionGuard moduleKey="businessWallets">
                    <BusinessWalletTxnHistoryPage />
                  </FinancePermissionGuard>
                }
              />
              <Route
                path="/business-cash-outs"
                element={
                  <FinancePermissionGuard moduleKey="businessWallets">
                    <BusinessCashOutsPage />
                  </FinancePermissionGuard>
                }
              />
              <Route
                path="/business-cash-outs/details/:requestId"
                element={
                  <FinancePermissionGuard moduleKey="businessWallets">
                    <BusinessCashOutDetailsPage />
                  </FinancePermissionGuard>
                }
              />
              {/* General Routes */}
              <Route
                path="/tax-settings"
                element={
                  <FinancePermissionGuard moduleKey="taxSettings">
                    <TaxSettingsPage />
                  </FinancePermissionGuard>
                }
              />
              <Route
                path="/tier-settings"
                element={
                  <FinancePermissionGuard moduleKey="tierSettings">
                    <TierSettingsPage />
                  </FinancePermissionGuard>
                }
              />
              <Route
                path="/ad-settings"
                element={
                  <FinancePermissionGuard moduleKey="adSettings">
                    <AdSettingsPage />
                  </FinancePermissionGuard>
                }
              />
              <Route
                path="/ad-settings/test"
                element={
                  <FinancePermissionGuard moduleKey="adSettings">
                    <AdSettingsTestModule />
                  </FinancePermissionGuard>
                }
              />
              <Route path="/bank-settings" element={<BankSettingsPage />} />
              <Route
                path="/currency-settings"
                element={
                  <FinancePermissionGuard moduleKey="limits">
                    <CurrencySettingsPage />
                  </FinancePermissionGuard>
                }
              />
              <Route
                path="/finance-roles"
                element={
                  <FinancePermissionGuard moduleKey="financeRoles">
                    <FinanceRolesPage />
                  </FinancePermissionGuard>
                }
              />
              <Route
                path="/finance-admins"
                element={
                  <FinancePermissionGuard moduleKey="financeAdmin">
                    <FinanceAdminsPage />
                  </FinancePermissionGuard>
                }
              />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Route>
          </Route>
          {/* If no route found redirect it to --> /404 */}
        </Routes>
      </AclGuard>
    </Suspense>
  );
}

export default App;
